import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar as CalendarIcon, Check, X, Clock, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { cn, getStatusColor, formatDate } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Attendance = () => {
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedClass, setSelectedClass] = useState('all');
  const [attendanceState, setAttendanceState] = useState({});
  const { canEdit } = useAuth();

  useEffect(() => {
    fetchClasses();
    fetchStudents();
  }, []);

  useEffect(() => {
    if (selectedDate) {
      fetchAttendance();
    }
  }, [selectedDate, selectedClass]);

  const fetchClasses = async () => {
    try {
      const response = await axios.get(`${API_URL}/classes`);
      setClasses(response.data);
    } catch (error) {
      console.error('Failed to fetch classes:', error);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await axios.get(`${API_URL}/students`);
      setStudents(response.data);
    } catch (error) {
      console.error('Failed to fetch students:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendance = async () => {
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const params = new URLSearchParams({ date_filter: dateStr });
      if (selectedClass !== 'all') {
        params.append('class_id', selectedClass);
      }
      const response = await axios.get(`${API_URL}/attendance?${params}`);
      setAttendanceRecords(response.data);
      
      // Initialize attendance state from records
      const state = {};
      response.data.forEach(record => {
        state[record.student_id] = record.status;
      });
      setAttendanceState(state);
    } catch (error) {
      console.error('Failed to fetch attendance:', error);
    }
  };

  const toggleAttendance = (studentId, status) => {
    setAttendanceState(prev => ({
      ...prev,
      [studentId]: prev[studentId] === status ? undefined : status
    }));
  };

  const saveAttendance = async () => {
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const attendances = Object.entries(attendanceState)
        .filter(([_, status]) => status)
        .map(([studentId, status]) => ({
          student_id: studentId,
          date: dateStr,
          status: status
        }));
      
      await axios.post(`${API_URL}/attendance/bulk`, attendances);
      fetchAttendance();
    } catch (error) {
      console.error('Failed to save attendance:', error);
    }
  };

  const filteredStudents = students.filter(s => 
    selectedClass === 'all' || s.class_id === selectedClass
  );

  const presentCount = Object.values(attendanceState).filter(s => s === 'present').length;
  const absentCount = Object.values(attendanceState).filter(s => s === 'absent').length;
  const lateCount = Object.values(attendanceState).filter(s => s === 'late').length;

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-10 bg-slate-200 rounded animate-pulse w-48" />
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-slate-200 rounded animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="attendance-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Attendance</h1>
          <p className="text-slate-600 mt-1">Track daily student attendance</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total</p>
              <p className="text-xl font-bold text-slate-900">{filteredStudents.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <Check className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Present</p>
              <p className="text-xl font-bold text-emerald-600">{presentCount}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
              <X className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Absent</p>
              <p className="text-xl font-bold text-red-600">{absentCount}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Late</p>
              <p className="text-xl font-bold text-amber-600">{lateCount}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-full sm:w-auto justify-start" data-testid="date-picker">
              <CalendarIcon className="w-4 h-4 mr-2" />
              {format(selectedDate, 'PPP')}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              initialFocus
            />
          </PopoverContent>
        </Popover>

        <Select value={selectedClass} onValueChange={setSelectedClass}>
          <SelectTrigger className="w-full sm:w-48" data-testid="class-filter">
            <SelectValue placeholder="Filter by class" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Classes</SelectItem>
            {classes.map((cls) => (
              <SelectItem key={cls.id} value={cls.id}>{cls.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {canEdit() && (
          <Button 
            onClick={saveAttendance}
            className="bg-indigo-600 hover:bg-indigo-700 text-white sm:ml-auto"
            data-testid="save-attendance-btn"
          >
            Save Attendance
          </Button>
        )}
      </div>

      {/* Attendance Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Admission No</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Student Name</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Class</th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Present</th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Absent</th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Late</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredStudents.length > 0 ? (
                filteredStudents.map((student) => (
                  <tr key={student.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-6 py-4 text-sm font-medium text-slate-900">{student.admission_no}</td>
                    <td className="px-6 py-4 text-sm text-slate-700">{student.name}</td>
                    <td className="px-6 py-4 text-sm text-slate-700">{student.class_name || '-'}</td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => toggleAttendance(student.id, 'present')}
                        disabled={!canEdit()}
                        className={cn(
                          'w-8 h-8 rounded-full flex items-center justify-center transition-colors',
                          attendanceState[student.id] === 'present'
                            ? 'bg-emerald-500 text-white'
                            : 'bg-slate-100 text-slate-400 hover:bg-emerald-100 hover:text-emerald-600'
                        )}
                        data-testid={`present-${student.id}`}
                      >
                        <Check className="w-4 h-4" />
                      </button>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => toggleAttendance(student.id, 'absent')}
                        disabled={!canEdit()}
                        className={cn(
                          'w-8 h-8 rounded-full flex items-center justify-center transition-colors',
                          attendanceState[student.id] === 'absent'
                            ? 'bg-red-500 text-white'
                            : 'bg-slate-100 text-slate-400 hover:bg-red-100 hover:text-red-600'
                        )}
                        data-testid={`absent-${student.id}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => toggleAttendance(student.id, 'late')}
                        disabled={!canEdit()}
                        className={cn(
                          'w-8 h-8 rounded-full flex items-center justify-center transition-colors',
                          attendanceState[student.id] === 'late'
                            ? 'bg-amber-500 text-white'
                            : 'bg-slate-100 text-slate-400 hover:bg-amber-100 hover:text-amber-600'
                        )}
                        data-testid={`late-${student.id}`}
                      >
                        <Clock className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <div className="text-slate-500">
                      <Users className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                      <p className="font-medium">No students found</p>
                      <p className="text-sm mt-1">Add students to mark attendance</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Attendance;
